import { clickNote, clickNoteFromEvent, describeClickTarget } from "./shortlinkMetrics";

export const HCVA_EMBED_CLICK_MSG = "hcva:embed_click";

/**
 * When this app is loaded inside the redirect offer iframe, forward every
 * trusted click to the parent so Tracing can log it.
 */
export function startEmbedClickReporter() {
  if (typeof window === "undefined") return () => {};

  let inIframe = false;
  try {
    inIframe = window.parent != null && window.parent !== window;
  } catch {
    inIframe = true;
  }
  if (!inIframe) return () => {};

  let isHcvaEmbed = false;
  try {
    isHcvaEmbed = new URLSearchParams(window.location.search).has("hcva_embed");
  } catch {
    isHcvaEmbed = false;
  }
  if (!isHcvaEmbed) return () => {};

  const onClick = (e) => {
    if (!e?.isTrusted) return;
    const { label, detail } = describeClickTarget(e.target);
    try {
      window.parent.postMessage(
        {
          type: HCVA_EMBED_CLICK_MSG,
          label,
          detail,
          x: Math.round(e.clientX || 0),
          y: Math.round(e.clientY || 0),
          path: window.location.pathname || "",
        },
        "*"
      );
    } catch {
      /* ignore */
    }
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

/**
 * Parent-side: listen for offer-iframe click messages and bind same-origin
 * contentDocument clicks when possible. Calls onNote(visitNoteString).
 */
export function bindOfferIframeClickTracking(iframe, onNote) {
  if (!iframe || typeof onNote !== "function") return () => {};

  let sameOriginBound = false;
  let removeDocListener = () => {};

  const onMessage = (event) => {
    if (sameOriginBound) return;
    const data = event?.data;
    if (!data || data.type !== HCVA_EMBED_CLICK_MSG) return;
    if (iframe.contentWindow && event.source && event.source !== iframe.contentWindow) {
      return;
    }
    onNote(
      clickNote({
        label: data.label || "",
        detail: data.detail || "",
        x: data.x,
        y: data.y,
        inOffer: true,
      })
    );
  };

  const tryBindSameOrigin = () => {
    removeDocListener();
    removeDocListener = () => {};
    sameOriginBound = false;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const onDocClick = (e) => {
        if (!e?.isTrusted) return;
        onNote(clickNoteFromEvent(e, { inOffer: true }));
      };
      doc.addEventListener("click", onDocClick, true);
      sameOriginBound = true;
      removeDocListener = () => {
        try {
          doc.removeEventListener("click", onDocClick, true);
        } catch {
          /* ignore */
        }
      };
    } catch {
      /* cross-origin — postMessage path only */
    }
  };

  window.addEventListener("message", onMessage);
  const onLoad = () => tryBindSameOrigin();
  iframe.addEventListener("load", onLoad);
  tryBindSameOrigin();

  return () => {
    window.removeEventListener("message", onMessage);
    iframe.removeEventListener("load", onLoad);
    removeDocListener();
  };
}
